import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import useSEO, { SITE_URL } from '@/hooks/useSEO';

export default function ArticlePage() {
  const { t, i18n } = useTranslation('common');
  const { slug } = useParams<{ slug: string }>();
  const currentLang = i18n.language || 'en';

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  const articleTitle = article ? t(article.key) : 'Article Not Found';
  const articleDesc = article ? t(article.descKey) : '';

  useSEO({
    title: `${articleTitle} – AllChinaBuy Spreadsheet`,
    description: articleDesc || t('metaArticleDesc'),
    canonical: `${SITE_URL}/${currentLang}/blog/${slug || ''}`,
    lang: currentLang,
    keywords: `${articleTitle}, AllChinaBuy spreadsheet, AllChinaBuy review, AllChinaBuy buying guide`,
    ogType: 'article',
    ogTitle: articleTitle,
    ogDesc: articleDesc,
    ogImage: article ? article.image : undefined,
    twitterImage: article ? article.image : undefined,
    structuredData: article ? {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: articleTitle,
      description: articleDesc,
      url: `${SITE_URL}/${currentLang}/blog/${article.slug}`,
      image: article.image,
      datePublished: article.date,
      dateModified: '2026-08-25',
      author: { '@type': 'Organization', name: 'AllChinaBuy Spreadsheet Editorial Team' },
      publisher: { '@type': 'Organization', name: 'AllChinaBuy Spreadsheet', url: SITE_URL },
    } : undefined,
  });

  useEffect(() => {
    document.documentElement.lang = currentLang;
  }, [currentLang]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="container-main pt-32 pb-20 text-center">
          <h1 className="font-heading text-2xl text-foreground-900 mb-4">Article Not Found</h1>
          <Link to={`/${currentLang}`} className="btn-primary">
            {t('backToHome')}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20">
        <nav aria-label="Breadcrumb" className="border-b border-background-200">
          <div className="container-main py-3">
            <ol className="flex items-center gap-2 text-sm">
              <li>
                <Link to={`/${currentLang}`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                  {t('breadcrumbHome')}
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-foreground-300 text-xs" /></li>
              <li>
                <Link to={`/${currentLang}/blog`} className="text-foreground-500 hover:text-primary-600 transition-colors">
                  {t('breadcrumbBlog')}
                </Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-foreground-300 text-xs" /></li>
              <li className="text-foreground-700 font-medium">{articleTitle}</li>
            </ol>
          </div>
        </nav>

        <article className="container-main py-8 md:py-12">
          <div className="max-w-3xl mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-primary-100 text-primary-800">
                  {article.category}
                </span>
                <span className="text-xs text-foreground-400">{article.date}</span>
                <span className="text-xs text-foreground-400">{article.readTime} read</span>
              </div>
              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4">
                {articleTitle}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {articleDesc}
              </p>
            </header>

            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
              <img
                src={article.image}
                alt={articleTitle}
                className="w-full h-full object-cover"
              />
            </div>

            <div className="article-content">
              <h2 id="quick-answer">{t('quickAnswer')}</h2>
              <p>
                The <strong>AllChinaBuy spreadsheet</strong> is a powerful resource for buyers who want to organize product research, compare sellers, track shipping costs, and discover quality-checked items. Whether you are new to buying from China or an experienced shopper, this spreadsheet hub helps you make informed purchasing decisions with structured data and practical guides.
              </p>

              <h2 id="detailed-explanation">{t('detailedExplanation')}</h2>
              <p>
                When shopping through AllChinaBuy, buyers often face the challenge of organizing vast amounts of product information. The AllChinaBuy spreadsheet addresses this by providing a structured format where you can track product categories, seller ratings, quality control status, shipping methods, and price comparisons all in one place.
              </p>
              <p>
                Our resource hub covers every aspect of the buying journey. From discovering trending products in categories like clothing, shoes, accessories, and streetwear, to understanding shipping costs and delivery times across different methods. We also provide detailed QC guides to help you evaluate product quality before committing to a purchase.
              </p>

              <h2 id="practical-guide">{t('practicalGuide')}</h2>
              <p>
                To use the AllChinaBuy spreadsheet effectively, start by identifying your target product category. Browse our <Link to={`/${currentLang}`}>homepage categories</Link> including shoes, hoodies, t-shirts, jackets, pants, headwear, and accessories. Each category links to curated product lists on our main website where you can explore options.
              </p>
              <p>
                Next, research the seller. Look for trusted sellers with consistent positive reviews and quality control records. Use our seller comparison resources to evaluate reliability, communication quality, and shipping consistency. Always check the latest coupon and discount codes before placing an order to maximize savings.
              </p>

              <h2 id="pros-cons">{t('prosAndCons')}</h2>
              <table>
                <thead>
                  <tr>
                    <th>{t('feature')}</th>
                    <th>Pros</th>
                    <th>Cons</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Product Discovery</strong></td>
                    <td>Organized categories, trending items, easy browsing</td>
                    <td>Inventory changes frequently, stock may vary</td>
                  </tr>
                  <tr>
                    <td><strong>Shipping Options</strong></td>
                    <td>Multiple methods, tracking available, insurance options</td>
                    <td>Costs vary, delivery times depend on region</td>
                  </tr>
                  <tr>
                    <td><strong>QC Process</strong></td>
                    <td>Pre-shipment inspection, photo verification</td>
                    <td>Additional processing time, may not catch all issues</td>
                  </tr>
                  <tr>
                    <td><strong>Discount Codes</strong></td>
                    <td>Regular promotions, seasonal sales, bulk discounts</td>
                    <td>Limited validity, terms and conditions apply</td>
                  </tr>
                </tbody>
              </table>

              <h2 id="common-mistakes">{t('commonMistakes')}</h2>
              <ul>
                <li><strong>Not verifying seller reputation</strong> before placing large orders. Always check reviews and ratings.</li>
                <li><strong>Ignoring shipping costs</strong> when calculating total price. Factor in customs and duties.</li>
                <li><strong>Skipping QC photos</strong> without inspecting carefully. Request detailed images for high-value items.</li>
                <li><strong>Missing discount codes</strong> by not checking current promotions before checkout.</li>
                <li><strong>Underestimating delivery times</strong> during peak seasons. Plan ahead for holidays and events.</li>
              </ul>

              <h2 id="safety-notes">{t('safetyNotes')}</h2>
              <p>
                Always practice safe browsing habits when researching products and sellers. Use secure payment methods, verify website URLs, and never share personal financial information outside of trusted platforms. Visit our <Link to="/safety">Safety Center</Link> for comprehensive security guidelines.
              </p>

              <h2 id="faq">{t('faq')}</h2>
              <h3>What is the AllChinaBuy spreadsheet used for?</h3>
              <p>
                The AllChinaBuy spreadsheet is used to organize product research, compare sellers, track shipping costs, and discover quality-checked items across multiple categories. It serves as a centralized resource hub for informed purchasing decisions.
              </p>
              <h3>How often is the spreadsheet updated?</h3>
              <p>
                We update our resources regularly to reflect current product availability, pricing changes, and shipping information. Check the last updated date on each resource page for the most recent information.
              </p>
              <h3>Can I trust the sellers listed?</h3>
              <p>
                While we provide research tools and seller information, we recommend independently verifying seller reviews, ratings, and transaction history before making any purchase. Use our guides to learn how to evaluate seller credibility.
              </p>
              <h3>Are there coupons available?</h3>
              <p>
                Yes, AllChinaBuy regularly offers coupons and promotional codes. Visit our coupons section or check the main website for current active discount codes. Note that codes have expiration dates and usage terms.
              </p>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200">
              <div className="flex items-center justify-between text-sm text-foreground-400">
                <div className="flex items-center gap-2">
                  <i className="ri-user-line" />
                  <span>{t('author')}</span>
                </div>
                <div className="flex items-center gap-4">
                  <span>{t('published')}: {article.date}</span>
                  <span>{t('lastUpdated')}: {t('lastModified')}</span>
                </div>
              </div>
            </div>

            <div className="mt-8 p-6 rounded-lg bg-primary-50 border border-primary-200">
              <p className="text-sm text-foreground-700 mb-4">
                {t('ctaFunnel')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={`/${currentLang}`} className="btn-primary gap-2 text-sm">
                  <i className="ri-home-line" />
                  {t('backToHome')}
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="btn-secondary gap-2 text-sm"
                >
                  <i className="ri-external-link-line" />
                  {t('visitMain')}
                </a>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 border-t border-background-200">
            <div className="container-main py-10 md:py-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
                {t('relatedArticles')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((ra) => (
                  <article key={ra.slug} className="card card-hover overflow-hidden">
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={t(ra.key)}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-5">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800 mb-2">
                        {ra.category}
                      </span>
                      <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-2 line-clamp-2">
                        {t(ra.key)}
                      </h3>
                      <Link
                        to={`/${currentLang}/blog/${ra.slug}`}
                        className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        {t('readMore')}
                        <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}